import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './StaffDetailsPage.css';
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../../utils/apiConfig';
import { COMPANIES } from '../../../utils/companyData';
import { db, auth } from '../../../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { useAuth } from '../../../contexts/AuthContext';

const StaffDetailsPage = () => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({ 
    name: '', 
    address: '', 
    email: '', 
    mobile: '', 
    role: 'staff', 
    department: '',
    company: '',
    password: ''
  });
  const [staff, setStaff] = useState([]);
  const [staffEndpointAvailable, setStaffEndpointAvailable] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  
  const loadFirebaseStaffData = useCallback(async () => {
    try {
      console.log('🔥 Loading Firebase users data...');
      console.log('🔥 Current user:', currentUser);
      console.log('🔥 Firebase db instance:', db);
      
      const usersCollection = collection(db, 'users');
      console.log('🔥 Users collection reference:', usersCollection);
      
      const snapshot = await getDocs(usersCollection);
      console.log('🔥 Firebase users snapshot size:', snapshot.size);
      console.log('🔥 Firebase users snapshot empty:', snapshot.empty);
      console.log('🔥 Firebase users snapshot metadata:', snapshot.metadata);
      
      const firebaseUsers = [];
      snapshot.forEach((doc) => {
        const userData = { _id: doc.id, ...doc.data() };
        console.log('🔥 Firebase user document:', doc.id, userData);
        // Ensure _id is properly set
        if (!userData._id) {
          userData._id = doc.id;
        }
        firebaseUsers.push(userData);
      });
      console.log('🔥 Total Firebase users loaded:', firebaseUsers.length);
      console.log('🔥 Firebase users data:', firebaseUsers);
      return firebaseUsers;
    } catch (error) {
      console.error('❌ Failed to load Firebase users data:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      return [];
    }
  }, [currentUser]);


  // Add staff member to Firebase (both Auth and Firestore)
  const addStaffToFirebase = async (staffData) => {
    try {
      // 1. Create Firebase Authentication user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        staffData.email, 
        staffData.password
      );
      
      const firebaseUser = userCredential.user;
      
      // 2. Update the user's display name
      await updateProfile(firebaseUser, {
        displayName: staffData.name
      });
      
      // 3. Create user document in Firestore
      const userDocData = {
        name: staffData.name,
        email: staffData.email,
        mobile: staffData.mobile,
        role: staffData.role,
        department: staffData.department,
        address: staffData.address,
        company: staffData.company,
        isActive: true,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid,
        // Don't store password in Firestore for security
      };
      
      const usersCollection = collection(db, 'users');
      const docRef = await addDoc(usersCollection, userDocData);
      
      return {
        firebaseAuthId: firebaseUser.uid,
        firestoreId: docRef.id
      };
    } catch (error) {
      console.error('Failed to add staff to Firebase:', error);
      throw error;
    }
  };

  // Update staff member in Firebase users collection
  const updateStaffInFirebase = async (staffId, staffData) => {
    try {
      const userDoc = doc(db, 'users', staffId);
      await updateDoc(userDoc, staffData);
    } catch (error) {
      console.error('Failed to update staff in Firebase:', error);
      throw error;
    }
  };

  // Delete staff member from Firebase users collection
  const deleteStaffFromFirebase = async (staffId) => {
    try {
      // Validate staffId
      if (!staffId || typeof staffId !== 'string') {
        throw new Error(`Invalid staff ID: ${staffId}`);
      }
      
      const userDoc = doc(db, 'users', staffId);
      await deleteDoc(userDoc);
    } catch (error) {
      console.error('Failed to delete staff from Firebase:', error);
      throw error;
    }
  };

  const [companies] = useState(COMPANIES);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const tableRef = useRef(null);
  const navigate = useNavigate();


  const API_URL = `${API_CONFIG.BASE_URL}/api`;

useEffect(() => {
const fetchData = async () => {
  if (!currentUser) {
    setError('No authentication found. Please log in.');
    navigate('/login');
    return;
  }
  try {
    setLoading(true);
    const idToken = await currentUser.getIdToken(true);
    const headers = { Authorization: `Bearer ${idToken}` };
    
    // Companies are now hardcoded in state, so we only need to fetch staff
    let staffData = [];
    
    try {
      const staffRes = await axios.get(`${API_URL}/staff`, { headers });
      staffData = Array.isArray(staffRes.data) ? staffRes.data : [];
      setStaffEndpointAvailable(true);
    } catch (staffErr) {
      console.error('Failed to fetch staff:', staffErr);
      if (staffErr.response?.status === 404 || staffErr.message?.includes('404') || staffErr.message?.includes('Not Found')) {
        console.warn('Staff endpoint not available on server - loading Firebase users data');
        // Load Firebase users data as fallback
        staffData = await loadFirebaseStaffData();
        console.log('🔥 Firebase users data loaded in fetchData:', staffData);
        setStaffEndpointAvailable(false);
      } else if (staffErr.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      } else if (staffErr.response?.status === 403) {
        setError('Access denied: You need HR Admin or Super Admin permissions to view staff.');
        return;
      } else {
        setError(`Failed to load staff: ${staffErr.response?.data?.error || staffErr.message}`);
      }
    }
    
    const staffWithCompanies = staffData.map((user, index) => {
      // Map user data to staff format
      const staffMember = {
        _id: user._id || user.id || `temp-${index}`,
        name: user.name || user.displayName || 'Unknown',
        email: user.email || '',
        mobile: user.phoneNumber || user.mobile || '',
        role: user.role || 'staff',
        department: user.department || '',
        address: user.address || '',
        company: user.company || null,
        isActive: user.isActive !== false, // Default to true if not specified
        createdAt: user.createdAt || user.created_at,
        lastLogin: user.lastLogin || user.last_login
      };
      
      // Find company if user has company data
      const company = companies.find(c => 
        c._id === staffMember.company?._id || 
        c._id === staffMember.company ||
        c.name === staffMember.company?.name
      );
      
      return {
        ...staffMember,
        company: company ? { _id: company._id, name: company.name } : null
      };
    });
    
    // console.log('🔥 Final processed staff data:', staffWithCompanies);
    setStaff(staffWithCompanies);
    setError(''); // Clear any previous errors since companies are always available
  } catch (err) {
    console.error('Unexpected error:', err);
    setError(`Unexpected error: ${err.message}`);
  } finally {
    setLoading(false);
  }
};
fetchData();
  
  }, [currentUser, navigate, API_URL, companies, loadFirebaseStaffData]);

  const handleInput = (field, value) => {
    if (field === 'role' && (value === 'super admin' || value === 'staff')) {
      setFormData({ ...formData, [field]: value, department: '' });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      handleUpdate(editingId);
    } else {
      handleCreate();
    }
  };

  const handleCreate = async () => {
    if (!currentUser) {
      setError('No authentication found. Please log in.');
      return;
    }
  if (!formData.name || !formData.email || !formData.mobile || !formData.role || !formData.company) {
      setError('All fields are required except address and department (unless role is admin)');
      return;
    }
    if (formData.role === 'admin' && !formData.department) {
      setError('Department is required for admin role.');
      return;
    }
    // Password validation - required for new staff, optional for editing
    if (!editingId && !formData.password) {
      setError('Password is required for new staff members.');
      return;
    }
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    try {
      const payload = {
        name: formData.name,
        address: formData.address,
        email: formData.email,
        mobile: formData.mobile,
        role: formData.role,
        company: formData.company

      };
      if (formData.role === 'admin') {
        payload.department = formData.department;
      }
      const idToken = await currentUser.getIdToken(true);
      const response = await axios.post(`${API_URL}/staff`, payload, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
         const company = companies.find(c => c._id === formData.company);
      const newStaff = {
        ...response.data,
        company: { _id: company._id, name: company.name } // Add company name for display
      };

      setStaff([...staff, newStaff]);
      resetForm();
      setError('');
    } catch (err) {
      console.error('Failed to create staff member:', err);
      if (err.response?.status === 404 || err.message?.includes('404') || err.message?.includes('Not Found')) {
        // API endpoint not available - save to Firebase as fallback
        const company = companies.find(c => c._id === formData.company);
        const newStaffData = {
          name: formData.name,
          address: formData.address,
          email: formData.email,
          mobile: formData.mobile,
          role: formData.role,
          department: formData.department,
          company: { _id: company._id, name: company.name },
          password: formData.password,
          createdAt: new Date().toISOString(),
          isFirebase: true // Flag to indicate this is Firebase data
        };
        
        try {
          // Add to Firebase (both Auth and Firestore)
          const result = await addStaffToFirebase(newStaffData);
          const newStaff = { 
            _id: result.firestoreId, 
            ...newStaffData,
            firebaseAuthId: result.firebaseAuthId
          };
          
          // Add to current staff list
          setStaff([...staff, newStaff]);
          resetForm();
          setError('');
          
          // Show success message with Firebase note
          setError('Staff member added to Firebase Authentication and Firestore (API endpoint unavailable). User can now login with provided credentials.');
          setTimeout(() => {
            setError('');
          }, 5000); // Clear message after 5 seconds
        } catch (firebaseError) {
          console.error('Failed to save to Firebase:', firebaseError);
          if (firebaseError.code === 'auth/email-already-in-use') {
            setError('Email is already in use. Please use a different email address.');
          } else if (firebaseError.code === 'auth/weak-password') {
            setError('Password is too weak. Please use a stronger password.');
          } else {
            setError(`Failed to save staff member to Firebase: ${firebaseError.message}`);
          }
        }
      } else {
      setError(err.response?.data?.error || 'Failed to create staff member');
      }
    }
  };

  const refreshStaffData = async () => {
  try {
    const idToken = await currentUser.getIdToken(true);
    const headers = { Authorization: `Bearer ${idToken}` };
    
    // Companies are hardcoded, so we only need to refresh staff data
    let staffData = [];
    
    try {
      const staffRes = await axios.get(`${API_URL}/staff`, { headers });
      staffData = Array.isArray(staffRes.data) ? staffRes.data : [];
      setStaffEndpointAvailable(true);
    } catch (staffErr) {
      console.error('Failed to refresh staff:', staffErr);
      if (staffErr.response?.status === 404 || staffErr.message?.includes('404') || staffErr.message?.includes('Not Found')) {
        console.warn('Staff endpoint not available during refresh - loading Firebase staff data');
        // Load Firebase staff data as fallback
        staffData = await loadFirebaseStaffData();
        setStaffEndpointAvailable(false);
      }
    }
    
    const updatedStaff = staffData.map(staffMember => {
      const company = companies.find(c => c._id === staffMember.company?._id || c._id === staffMember.company);
      return {
        ...staffMember,
        company: company ? { _id: company._id, name: company.name } : null
      };
    });
    
    setStaff(updatedStaff);
  } catch (err) {
    console.error('Failed to refresh staff data:', err);
    setError('Failed to refresh staff data');
  }
  };
  
  const handleUpdate = async (id) => {
  if (!formData.company) {
    setError('Company selection is required');
    return;
  }

  try {
    const payload = {
      name: formData.name,
      address: formData.address,
      email: formData.email,
      mobile: formData.mobile,
      role: formData.role,
      company: formData.company
    };

    if (formData.role === 'admin') {
      payload.department = formData.department;
    }
    
    const idToken = await currentUser.getIdToken(true);
    await axios.put(`${API_URL}/staff/${id}`, payload, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    
    await refreshStaffData(); // This will now properly refresh all data
    setEditingId(null);
    resetForm();
    setError('');
  } catch (err) {
    console.error('Failed to update staff:', err);
      if (err.response?.status === 404 || err.message?.includes('404') || err.message?.includes('Not Found')) {
        
        // API endpoint not available - update in Firebase as fallback
        const company = companies.find(c => c._id === formData.company);
        const updatedStaffData = {
          name: formData.name,
          address: formData.address,
          email: formData.email,
          mobile: formData.mobile,
          role: formData.role,
          department: formData.department,
          company: { _id: company._id, name: company.name },
          updatedAt: new Date().toISOString(),
          isFirebase: true // Flag to indicate this is Firebase data
        };
        
        try {
          // Update in Firebase
          await updateStaffInFirebase(id, updatedStaffData);
          
          // Update current staff list
          const updatedStaffList = staff.map(staffMember => 
            staffMember._id === id ? { _id: id, ...updatedStaffData } : staffMember
          );
          setStaff(updatedStaffList);
          
          setEditingId(null);
          resetForm();
          setError('');
          
          // Show success message with Firebase note
          setError('Staff member updated in Firebase (API endpoint unavailable). Data will be synced when server is available.');
          setTimeout(() => {
            setError('');
          }, 3000); // Clear message after 3 seconds
        } catch (firebaseError) {
          console.error('Failed to update in Firebase:', firebaseError);
          setError('Failed to update staff member in Firebase. Please try again.');
        }
    } else {
    setError(err.response?.data?.error || 'Failed to update staff');
    }
  }
  };

  const handleDelete = async (id) => {
    if (!currentUser) {
      setError('No authentication found. Please log in.');
      return;
    }
    try {
      const idToken = await currentUser.getIdToken(true);
      await axios.delete(`${API_URL}/staff/${id}`, { headers: { Authorization: `Bearer ${idToken}` } });
      setStaff((prev) => prev.filter((item) => item._id !== id));
      setError('');
    } catch (err) {
      console.error('Failed to delete staff:', err);
      if (err.response?.status === 404 || err.message?.includes('404') || err.message?.includes('Not Found')) {
        
        try {
          // Check if this staff member is stored in Firebase
          const staffMember = staff.find(s => s._id === id);
          if (staffMember && staffMember.isFirebase) {
            // API endpoint not available - delete from Firebase as fallback
            await deleteStaffFromFirebase(id);
            setError('Staff member deleted from Firebase (API endpoint unavailable). Data will be synced when server is available.');
          } else {
            // Staff member is not in Firebase, just remove from local state
            setError('Staff member removed from local list (API endpoint unavailable).');
          }
          
          // Remove from current staff list
          setStaff((prev) => prev.filter((item) => item._id !== id));
          setError('');
          
          setTimeout(() => {
            setError('');
          }, 3000); // Clear message after 3 seconds
        } catch (firebaseError) {
          console.error('Failed to delete from Firebase:', firebaseError);
          setError('Failed to delete staff member from Firebase. Please try again.');
        }
      } else {
      setError(err.response?.data?.error || 'Failed to delete staff');
      }
    }
  };

  const handleEdit = (staffMember) => {
  setEditingId(staffMember._id);
  setFormData({
    name: staffMember.name,
    address: staffMember.address || '',
    email: staffMember.email,
    mobile: staffMember.mobile,
    role: staffMember.role,
    department: staffMember.department || '',
    company: staffMember.company?._id || staffMember.company || '',
    password: '' // Don't pre-fill password for security
  });
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', email: '', mobile: '', role: 'staff', department: '', company: '', password: '' });
    setEditingId(null);
    setError('');
  };

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Get the table HTML
    const table = document.querySelector('.staff-tablee');
    if (!table) {
      alert('No table found to print');
      return;
    }
    
    // Clone the table and remove action column
    const tableClone = table.cloneNode(true);
    
    // Remove the Actions column (last column)
    const rows = tableClone.querySelectorAll('tr');
    rows.forEach(row => {
      const cells = row.querySelectorAll('td, th');
      if (cells.length > 6) {
        cells[cells.length - 1].remove(); // Remove the last cell (Actions column)
      }
    });
    
    // Get current date
      const currentDate = new Date().toLocaleString('en-US', {
        timeZone: 'Asia/Kolkata',
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    
    // Write the print content
    printWindow.document.write(`
      <html>
        <head>
          <title>Staff Details Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px; 
              font-size: 18px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 0;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              text-align: left;
              font-size: 12px;
            }
            th { 
              background-color: #f0f0f0; 
              font-weight: bold; 
              text-align: center;
            }
            .print-timestamp {
              text-align: right;
              margin-top: 20px;
              font-size: 10px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Staff Management - Staff List</h1>
          <p style="text-align: center; margin-bottom: 20px;">Generated on: ${new Date().toLocaleDateString()}</p>
          ${tableClone.outerHTML}
          <div class="print-timestamp">Printed on: ${currentDate} IST</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownload = () => {
    const table = document.querySelector('.staff-tablee');
    if (!table) {
      alert('No table found to download');
      return;
    }

    // Clone the table and remove all interactive elements
    const tableClone = table.cloneNode(true);
    
    // Remove ALL interactive elements more comprehensively
    const interactiveSelectors = [
      'button', 'input', 'select', 'textarea',
      '[onclick]', '[onchange]', '[oninput]', '[onmousedown]', '[onmouseup]',
      '.btn', '.button', '.form-control', '.form-select', '.input-group',
      '.actions', '.header-actions', '.controls', '.interactive',
      '.clickable', '.dropdown', '.nav', '.navbar', '.no-print'
    ];
    
    interactiveSelectors.forEach(selector => {
      const elements = tableClone.querySelectorAll(selector);
      elements.forEach(element => element.remove());
    });
    
    // Remove any elements with interactive classes or attributes
    const allElements = tableClone.querySelectorAll('*');
    allElements.forEach(element => {
      // Remove elements with interactive classes
      const classList = element.className;
      if (typeof classList === 'string') {
        const interactiveClasses = ['btn', 'button', 'input', 'select', 'form', 'interactive', 'clickable', 'actions', 'no-print'];
        const hasInteractiveClass = interactiveClasses.some(cls => classList.includes(cls));
        if (hasInteractiveClass) {
          element.remove();
          return;
        }
      }
      
      // Remove elements with event handlers
      const hasEventHandlers = element.onclick || element.onchange || element.oninput || 
                              element.onmousedown || element.onmouseup || element.onkeydown;
      if (hasEventHandlers) {
        element.remove();
        return;
      }
      
      // Remove elements with interactive attributes
      const interactiveAttrs = ['onclick', 'onchange', 'oninput', 'onmousedown', 'onmouseup', 'onkeydown'];
      const hasInteractiveAttr = interactiveAttrs.some(attr => element.hasAttribute(attr));
      if (hasInteractiveAttr) {
        element.remove();
      }
    });
    
    // Remove action columns and headers
    const actionHeaders = tableClone.querySelectorAll('th.actions, td.actions, .action-column');
    actionHeaders.forEach(element => element.remove());

    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Staff Details Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px; 
              font-size: 18px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 0;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              text-align: left;
              font-size: 12px;
            }
            th { 
              background-color: #f0f0f0; 
              font-weight: bold; 
              text-align: center;
            }
            button,
            input,
            select,
            textarea,
            .actions,
            .header-actions,
            .controls,
            .interactive,
            .clickable,
            .btn,
            .button,
            .form-control,
            .form-select,
            .input-group,
            .dropdown,
            .nav,
            .navbar,
            .no-print,
            [onclick],
            [onchange],
            [oninput],
            [onmousedown],
            [onmouseup],
            [onkeydown] { 
              display: none !important; 
              visibility: hidden !important;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Staff Details Report</h1>
          ${tableClone.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };
 
   
  if (loading) {
    return <div className="loading">Loading staff data...</div>;
  }
 
  return (
    <div className="staff-details-page">
      <div className="page-header no-print">
        <button className="staff-page-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2>Staff Management</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="staff-form-container no-print">
        <h3>{editingId ? 'Edit Staff Member' : 'Add New Staff Member'}</h3>
        
        <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Full Name*</label>
          <input 
            type="text" 
            value={formData.name}
            onChange={(e) => handleInput('name', e.target.value)}
            placeholder="Enter full name"
            required
          />
        </div>

        <div className="form-group">
          <label>Email*</label>
          <input 
            type="email" 
            value={formData.email}
            onChange={(e) => handleInput('email', e.target.value)}
            placeholder="Enter email address"
            autoComplete="email"
            required
          />
        </div>

        <div className="form-group">
          <label>Mobile Number*</label>
          <input 
            type="tel" 
            value={formData.mobile}
            onChange={(e) => handleInput('mobile', e.target.value)}
            placeholder="Enter mobile number"
            autoComplete="tel"
            required
          />
        </div>

        <div className="form-group">
          <label>Password{editingId ? ' (Leave blank to keep current)' : '*'}</label>
          <div className="password-input-container">
            <input 
              type={showPassword ? "text" : "password"} 
              value={formData.password}
              onChange={(e) => handleInput('password', e.target.value)}
              placeholder={editingId ? "Enter new password (leave blank to keep current)" : "Enter password (min 6 characters)"}
              required={!editingId}
              minLength={editingId ? 0 : 6}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Company*</label>
          <select
            value={formData.company}
            onChange={(e) => handleInput('company', e.target.value)}
            required
          >
            <option value="">Select Company</option>
            {companies.map(company => (
              <option key={company._id} value={company._id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Role*</label>
          <select
            value={formData.role}
            onChange={(e) => handleInput('role', e.target.value)}
            required
          >
            <option value="staff">Staff</option>
            <option value="admin">Admin</option>
            <option value="super admin">Super Admin</option>
          </select>
        </div>

        {formData.role === 'admin' && (
          <div className="form-group">
            <label>Department*</label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => handleInput('department', e.target.value)}
              placeholder="Enter department name"
              required
            />
          </div>
        )}

        <div className="form-group">
          <label>Address</label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleInput('address', e.target.value)}
            placeholder="Enter address"
          />
        </div>

        <div className="form-actions">
          {editingId ? (
            <>
              <button type="submit" className="btn-save">
                <FaSave /> Save Changes
              </button>
              <button type="button" className="btn-cancel" onClick={resetForm}>
                <FaTimes /> Cancel
              </button>
            </>
          ) : (
            <button type="submit" className="btn-add">
              <FaPlus /> Add Staff Member
            </button>
          )}
        </div>
        </form>
      </div>

      <div className="staff-list-container">
        <h3 className="no-print">Current Staff Members</h3>
        {!staffEndpointAvailable && (
          <div className="info-message no-print" style={{ 
            background: '#e3f2fd', 
            border: '1px solid #2196f3', 
            borderRadius: '4px', 
            padding: '8px 12px', 
            marginBottom: '16px',
            fontSize: '14px',
            color: '#1976d2'
          }}>
            ℹ️ Staff data endpoint is currently unavailable. Loading staff from Firebase users collection.
          </div>
        )}
        <div className="table-container">
          <div className="table-actions no-print">
            <button onClick={handlePrint} className="btn-print">
              Print List
            </button>
            <button onClick={handleDownload} className="btn-download">
              Download as PDF
            </button>
          </div>

          <div className="print-header">
            <h2>Staff Management - Staff List</h2>
            <p>Generated on: {new Date().toLocaleDateString()}</p>
          </div>

          <table className="staff-tablee" ref={tableRef}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Company</th>
              <th>Role</th>
              <th>Department</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.length > 0 ? (
              staff.map((staffMember, index) => {
                return (
                <tr key={staffMember._id || `staff-${index}`}>
                  <td>
                    {staffMember.name}
                    {staffMember.isFirebase && (
                      <span style={{ 
                        marginLeft: '8px', 
                        fontSize: '12px', 
                        color: '#4caf50', 
                        fontWeight: 'bold' 
                      }} title="Stored in Firebase">
                        🔥
                      </span>
                    )}
                  </td>
                  <td>{staffMember.email}</td>
                  <td>{staffMember.mobile}</td>
                  <td>{staffMember.company?.name || 'N/A'}</td>
                  <td>{staffMember.role}</td>
                  <td>{staffMember.department || 'N/A'}</td>
                  <td className="staff-actions no-print">
                    <button 
                      className="edit-btn"
                      onClick={() => handleEdit(staffMember)}
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDelete(staffMember._id || staffMember.id)}
                    >
                      <FaTrash />
                    </button>
            
                  </td>
                </tr>
                );
              })
            ) : (
              <tr key="no-staff">
                <td colSpan="7" className="no-data">
                  No staff members found
                </td>
              </tr>
            )}
          </tbody>
          </table>
        </div>
      </div>
      
      {/* Mobile Card Layout */}
      <div className="mobile-cards-container">
        {loading ? (
          <div className="mobile-loading">
            <div className="mobile-loading-spinner"></div>
          </div>
        ) : staff.length === 0 ? (
          <div className="mobile-empty-state">
            <div className="mobile-empty-state-icon">👥</div>
            <div className="mobile-empty-state-text">No staff members found</div>
            <div className="mobile-empty-state-subtext">Add your first staff member using the form above</div>
          </div>
        ) : (
          staff.map((staffMember, index) => (
            <div key={staffMember._id || `staff-${index}`} className="mobile-card">
              <div className="card-header">
                <h3 className="card-title">
                  {staffMember.name}
                  {staffMember.isFirebase && (
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '12px', 
                      color: '#007bff',
                      fontWeight: 'normal'
                    }}>
                      🔥
                    </span>
                  )}
                </h3>
                <span className={`card-status ${staffMember.isActive ? 'active' : 'terminated'}`}>
                  {staffMember.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="card-content">
                <div className="card-row">
                  <span className="card-label">Email</span>
                  <span className="card-value email">{staffMember.email || '-'}</span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Mobile</span>
                  <span className="card-value phone">{staffMember.mobile || '-'}</span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Company</span>
                  <span className="card-value">
                    {staffMember.company?.name || staffMember.company || '-'}
                  </span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Role</span>
                  <span className="card-value">
                    <span className={`card-role ${staffMember.role?.toLowerCase().replace(' ', '-')}`}>
                      {staffMember.role}
                    </span>
                  </span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Department</span>
                  <span className="card-value">
                    {staffMember.department || '-'}
                  </span>
                </div>
                
                {staffMember.address && (
                  <div className="card-row">
                    <span className="card-label">Address</span>
                    <span className="card-value">{staffMember.address}</span>
                  </div>
                )}
              </div>
              
              <div className="card-actions">
                <button 
                  onClick={() => handleEdit(staffMember)} 
                  className="card-action-btn edit"
                >
                  <FaEdit /> Edit
                </button>
                <button 
                  onClick={() => handleDelete(staffMember._id)} 
                  className="card-action-btn delete"
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StaffDetailsPage;